import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import Logger from "../../../services/Logger";
import StatsSummaryTile from "./StatsSummaryTile";

/** Workflow states worth a column; the rest are folded into the board count only. */
const STATE_COLUMNS = ["DRAFT", "READY_TO_REVIEW", "REVIEWED", "PUBLISHED", "DISABLED"];

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

const formatPercent = (value) =>
    Number.isFinite(Number(value)) ? `${Number(value).toFixed(1)}%` : "—";

const stateCount = (row, state) => Number(row?.byState?.[state]) || 0;

/**
 * Board inventory for puzzle types built from a size + difficulty pair (jigsaw, sliding):
 * how many boards exist per group, split by workflow state and free/paid.
 * `fetchStats` receives the language and must resolve the `/v1/stats/{type}/by-difficulty` payload.
 */
export default function PuzzleBoardInventoryPanel({
    language,
    fetchStats,
    sizeColumnLabel = "Board",
    showPieces = false,
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await fetchStats(language);
                if (!cancelled) {
                    setStats(result ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load puzzle board inventory stats:", err);
                if (!cancelled) {
                    setError("Failed to load puzzle statistics.");
                    setStats(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [fetchStats, language]);

    const groups = useMemo(() => (Array.isArray(stats?.groups) ? stats.groups : []), [stats]);
    const byState = stats?.byState ?? {};
    const totalBoards = Number(stats?.totalBoards) || 0;
    const totalFree = useMemo(
        () => groups.reduce((sum, row) => sum + (Number(row.free) || 0), 0),
        [groups]
    );

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {language} · boards grouped by size and difficulty
            </Typography>

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!loading && !error && stats && (
                <>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                            gap: 1,
                            mb: 3,
                        }}
                    >
                        <StatsSummaryTile label="Boards" value={formatNumber(totalBoards)} hint="All states" />
                        <StatsSummaryTile
                            label="Published"
                            value={formatNumber(byState.PUBLISHED)}
                            hint={formatPercent(totalBoards > 0 ? ((Number(byState.PUBLISHED) || 0) * 100) / totalBoards : 0)}
                        />
                        <StatsSummaryTile label="Reviewed" value={formatNumber(byState.REVIEWED)} hint="Waiting to publish" />
                        <StatsSummaryTile label="Draft" value={formatNumber(byState.DRAFT)} hint="Not reviewed yet" />
                        <StatsSummaryTile
                            label="Free boards"
                            value={formatNumber(totalFree)}
                            hint={formatPercent(totalBoards > 0 ? (totalFree * 100) / totalBoards : 0)}
                        />
                    </Box>

                    {groups.length === 0 ? (
                        <Alert severity="info">No boards found for {language}.</Alert>
                    ) : (
                        <>
                            <Card sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                                        Boards by size and difficulty
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Published boards are playable today; reviewed boards are the publish queue.
                                    </Typography>
                                    <BarChart
                                        height={320}
                                        xAxis={[{ scaleType: "band", data: groups.map((row) => row.band) }]}
                                        series={[
                                            {
                                                data: groups.map((row) => stateCount(row, "PUBLISHED")),
                                                label: "Published",
                                                color: "#3BA666",
                                            },
                                            {
                                                data: groups.map((row) => stateCount(row, "REVIEWED")),
                                                label: "Reviewed",
                                                color: "#1976d2",
                                            },
                                            {
                                                data: groups.map((row) => stateCount(row, "DRAFT")),
                                                label: "Draft",
                                                color: "#f0a202",
                                            },
                                        ]}
                                    />
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                        By state
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Board counts per group, with the free / paid split.
                                    </Typography>
                                    <TableContainer sx={{ overflowX: "auto" }}>
                                        <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600 }}>{sizeColumnLabel}</TableCell>
                                                    {showPieces && (
                                                        <TableCell align="right" sx={{ fontWeight: 600 }}>Pieces</TableCell>
                                                    )}
                                                    {STATE_COLUMNS.map((state) => (
                                                        <TableCell key={state} align="right" sx={{ fontWeight: 600 }}>
                                                            {state.replace(/_/g, " ")}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Boards</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Free</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Share</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {groups.map((row) => (
                                                    <TableRow key={row.band}>
                                                        <TableCell sx={{ fontWeight: 600 }}>{row.band}</TableCell>
                                                        {showPieces && (
                                                            <TableCell align="right">{formatNumber(row.pieces)}</TableCell>
                                                        )}
                                                        {STATE_COLUMNS.map((state) => (
                                                            <TableCell key={state} align="right">
                                                                {formatNumber(stateCount(row, state))}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell align="right">{formatNumber(row.boardCount)}</TableCell>
                                                        <TableCell align="right">{formatNumber(row.free)}</TableCell>
                                                        <TableCell align="right">{formatPercent(row.boardSharePercent)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>Sum</TableCell>
                                                    {showPieces && <TableCell />}
                                                    {STATE_COLUMNS.map((state) => (
                                                        <TableCell key={state} align="right" sx={{ fontWeight: 700 }}>
                                                            {formatNumber(byState[state])}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                        {formatNumber(totalBoards)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                        {formatNumber(totalFree)}
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </>
            )}
        </Box>
    );
}
